import { api } from './api'

type QueueItem = {
  id: string
  path: string
  method: string
  body: unknown
  headers: Record<string, string>
  createdAt: string
  attempts: number
  lastError?: string
  nextRetryAt?: string
}

const DB_NAME = 'pharmatrack-offline'
const STORE = 'requests'

function database(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1)
    request.onupgradeneeded = () => request.result.createObjectStore(STORE, { keyPath: 'id' })
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

export async function queueRequest(item: Omit<QueueItem, 'createdAt'|'attempts'>): Promise<void> {
  const db = await database()
  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(STORE, 'readwrite')
    transaction.objectStore(STORE).put({ ...item, attempts:0, createdAt: new Date().toISOString() })
    transaction.oncomplete = () => { window.dispatchEvent(new CustomEvent('offline:queue-changed')); resolve() }
    transaction.onerror = () => reject(transaction.error)
  })
}

async function all(): Promise<QueueItem[]> {
  const db = await database()
  return new Promise((resolve, reject) => {
    const request = db.transaction(STORE).objectStore(STORE).getAll()
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

async function remove(id: string): Promise<void> {
  const db = await database()
  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(STORE, 'readwrite')
    transaction.objectStore(STORE).delete(id)
    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(transaction.error)
  })
}

async function update(item:QueueItem):Promise<void>{const db=await database();await new Promise<void>((resolve,reject)=>{const tx=db.transaction(STORE,'readwrite');tx.objectStore(STORE).put(item);tx.oncomplete=()=>resolve();tx.onerror=()=>reject(tx.error)})}
export async function offlineQueueCount():Promise<number>{return (await all()).length}

let flushing=false

export async function flushOfflineQueue(): Promise<void> {
  if (!navigator.onLine||flushing) return
  flushing=true
  window.dispatchEvent(new CustomEvent('offline:sync-state',{detail:'syncing'}))
  for (const item of await all()) {
    if(item.nextRetryAt&&new Date(item.nextRetryAt)>new Date())continue
    try {
      await api(item.path, { method: item.method, body: JSON.stringify(item.body), headers: item.headers })
      await remove(item.id)
      window.dispatchEvent(new CustomEvent('offline:synced', { detail: item.id }))
      window.dispatchEvent(new CustomEvent('offline:queue-changed'))
    } catch (error) {
      item.attempts=(item.attempts||0)+1
      item.lastError=error instanceof Error?error.message:'Synchronisation impossible'
      item.nextRetryAt=new Date(Date.now()+Math.min(300000,1000*2**item.attempts)).toISOString()
      await update(item)
      window.dispatchEvent(new CustomEvent('offline:sync-error',{detail:item.lastError}))
    }
  }
  flushing=false
  window.dispatchEvent(new CustomEvent('offline:sync-state',{detail:'idle'}))
}
